import { useState, useEffect } from 'react';
import Dropzone from 'react-dropzone';
import clsx from 'clsx';
import axios from 'axios';
import HeightAnimation from '@/animations/height';
import checkSrc from '@/components/uploader/assets/check.png';
import fileSrc from '@/components/uploader/assets/file.png';
import trashGraySrc from '@/components/uploader/assets/trash-gray.png';
import trashBlueSrc from '@/components/uploader/assets/trash-blue.png';
import uploadSrc from '@/components/uploader/assets/upload.png';
import Button from '@/components/button';
import LoadingSticks from '@/components/loading-sticks';
import FilesRejectionMain from '@/modals/files-rejection/files-rejection-main';
import { useAppDispatch, useAppSelector } from '@/redux/store';
import { ConfigSlice } from '@/redux/config';
import { GeneralSlice } from '@/redux/general';
import { ModalSlice } from '@/redux/modal';
import Track from '@/utils/track';
import '@/components/uploader/uploader.css';

const MAX_SIZE = 512 * 1024 * 1024;

const Uploader = () => {
  const leadId = useAppSelector(GeneralSlice.selectors.leadId);
  const enableUploadFiles = useAppSelector(ConfigSlice.selectors.enableUploadFiles);
  const redirectToOnSubmit = useAppSelector(ConfigSlice.selectors.redirectToOnSubmit);
  const vollmachtApiUrl = useAppSelector(ConfigSlice.selectors.vollmachtApiUrl);
  const [alreadyUploadedFilesSize, setAlreadyUploadedFilesSize] = useState(0);
  const [filesToUpload, setFilesToUpload] = useState([]);
  const [status, setStatus] = useState('idle');
  const [wasRejected, setWasRejected] = useState(false);

  const dispatch = useAppDispatch();

  const fileSize = (size) => {
    const pow = Math.pow(10, 2);
    return !!Math.round(size / 1000000)
      ? `${Math.abs(Math.round((size / 1000000) * pow) / pow)}MB`
      : `${Math.abs(Math.round((size / 1000) * pow) / pow)}KB`;
  };

  const bytesToMegabytes = (bytes) => {
    const megabytes = bytes / (1024 * 1024);
    return parseFloat(megabytes.toFixed(1)) % 1 === 0 ? megabytes.toFixed(0) : megabytes.toFixed(1);
  };

  const toBase64 = (file) =>
    new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => {
        const base64String = reader.result.split(',')[1];
        resolve(base64String);
      };
      reader.onerror = reject;
    });

  const onReject = (rejectedFiles) => {
    Track.DataLayer.FileSizeRejected();
    setWasRejected(true);
    dispatch(
      ModalSlice.actions.openModal({
        main: <FilesRejectionMain rejectedFiles={rejectedFiles} size={MAX_SIZE} />,
        modifier: 'default',
      }),
    );
  };

  const onDrop = (acceptedFiles, rejectedFiles) => {
    const newFilesSize = acceptedFiles.reduce((size, file) => size + file.size, 0);
    const exceedMaxSize = alreadyUploadedFilesSize + newFilesSize > MAX_SIZE;
    if (exceedMaxSize) {
      onReject(
        acceptedFiles.map((acceptedFile) => ({
          file: acceptedFile,
          errors: [],
        })),
      );
    }
    if (rejectedFiles.length) {
      onReject(rejectedFiles);
    }
    if (!exceedMaxSize && acceptedFiles.length) {
      const newFilesToUpload = acceptedFiles.map((acceptedFile, i) => {
        const type = acceptedFile.type;
        const count = filesToUpload.length + i + 1;
        const hasSuchName = filesToUpload.find((file) => file.name === `${count}-${acceptedFile.name}`);
        const fileName = `${hasSuchName ? count + 1 : count}-${acceptedFile.name}`;
        return new File([acceptedFile], fileName, { type });
      });
      setFilesToUpload([...filesToUpload, ...newFilesToUpload]);
    }
  };

  const onDelete = (e, fileName) => {
    e.stopPropagation();
    const updatedFilesToUpload = filesToUpload.filter((file) => file.name !== fileName);
    setFilesToUpload(updatedFilesToUpload);
  };

  const onUpload = (e) => {
    e.stopPropagation();
    postFiles();
  };

  const postFiles = async () => {
    setStatus('pending');
    const objects = await Promise.all(
      filesToUpload.map(async (file) => {
        return {
          documentType: 'decision_letter',
          name: file.name,
          file: await toBase64(file),
        };
      }),
    );
    try {
      await axios.post(vollmachtApiUrl, {
        externalId: leadId,
        objects,
      });
      Track.DataLayer.LeadfunnelFileUploaded(wasRejected);
      Track.Experiments.LeadfunnelFileUploaded();
      if (enableUploadFiles && redirectToOnSubmit) {
        window.open(redirectToOnSubmit.replace('{externalId}', leadId), '_self');
      }
    } catch {
      setStatus('fulfilled');
    }
  };

  const handleNextBtn = (e) => {
    e.preventDefault();
    if (filesToUpload.length >= 1) {
      postFiles();
    } else {
      Track.DataLayer.NoDocumentUpload(wasRejected);
      Track.Experiments.NoDocumentUpload();
      window.open(redirectToOnSubmit.replace('{externalId}', leadId), '_self');
    }
  };

  useEffect(() => {
    const totalSize = filesToUpload.reduce((totalSize, file) => {
      return totalSize + file.size;
    }, 0);
    setAlreadyUploadedFilesSize(totalSize);
  }, [filesToUpload]);

  return (
    <>
      <div className='uploader'>
        <HeightAnimation on={status !== 'fulfilled'}>
          <Dropzone
            noKeyboard
            multiple
            maxSize={MAX_SIZE - alreadyUploadedFilesSize}
            accept={{
              'image/gif': ['.gif'],
              'image/png': ['.png'],
              'image/jpg': ['.jpg'],
              'image/jpeg': ['.jpeg'],
              'application/pdf': ['.pdf'],
            }}
            onDrop={(acceptedFiles, rejectedFiles) => onDrop(acceptedFiles, rejectedFiles)}
          >
            {({ getRootProps, getInputProps }) => (
              <div {...getRootProps()} className='uploader__content'>
                <input {...getInputProps()} />
                <div className={clsx('uploader__wrapper', { 'uploader__wrapper--pending': status === 'pending' })}>
                  <div className='uploader__drop-title'>
                    <strong>Zum Hochladen Datei(en) hier ablegen oder hier klicken</strong>
                  </div>
                  <ul className='uploader__file-list'>
                    {filesToUpload.map((file, i) => (
                      <li key={`${file.name}-${i}`} className='uploader__file'>
                        <img className='uploader__file-icon' alt='file' src={fileSrc} />
                        <span className='uploader__file-text'>
                          {file.name} - {fileSize(file.size)}
                        </span>
                        <button
                          type='button'
                          tabIndex={0}
                          className='uploader__delete'
                          onClick={(e) => onDelete(e, file.name)}
                        >
                          <img alt='trash' src={trashGraySrc} />
                          <img alt='trash' src={trashBlueSrc} />
                        </button>
                      </li>
                    ))}
                  </ul>
                  <div className='uploader__drop-area'>
                    <img alt='upload' src={uploadSrc} />
                    <p>
                      Datei(en) auswählen /<br />
                      Dokumentenseite abfotografieren
                    </p>
                    <p>
                      Maximale gesamte Dateigröße: {bytesToMegabytes(MAX_SIZE)} MB. Unterstützte Dateitypen: PDF, JPG,
                      GIF, PNG.
                    </p>
                  </div>
                  {!!filesToUpload.length && (
                    <>
                      <button type='button' tabIndex={0} className='uploader__button' onClick={onUpload}>
                        Bescheid übermitteln
                      </button>
                    </>
                  )}
                </div>
                {status === 'pending' ? (
                  <div className='uploader__loader'>
                    <LoadingSticks />
                  </div>
                ) : null}
              </div>
            )}
          </Dropzone>
        </HeightAnimation>
        {status === 'fulfilled' ? (
          <div className='uploader__uploaded-files'>
            <h6>Bereits übermittelt</h6>
            <div>
              <span>Document</span>
              <img alt='check' src={checkSrc} />
            </div>
          </div>
        ) : null}
      </div>
      <div
        style={{
          margin: '40px 0 0 0',
          lineHeight: '1.2',
          fontFamily: 'Rubik',
          fontWeight: '300',
          fontSize: '14px',
          color: '#302576',
          textAlign: 'center',
        }}
      >
        Haben Sie Ihren Bescheid nicht zur Hand? Dann können Sie diesen Schritt auch <strong>später nachholen</strong>.
      </div>
      <div style={{ marginTop: '30px' }}>
        <Button
          id='go-next-vollmacht'
          className='button-next'
          color='primary'
          icon='arrow'
          iconPosition='after'
          onClick={handleNextBtn}
        >
          Weiter
        </Button>
      </div>
    </>
  );
};

Uploader.displayName = 'Uploader';

export default Uploader;
